import React, {useEffect, useRef, useState} from "react";
import {BrowserRouter, Route, Switch} from "react-router-dom";
import "../src/css/global.css";

import MainSearchPage from "./page/search/MainSearchPage";
import CompanySearchPage from "./page/search/CompanySearchPage";
import Header from "./component/Header";
import axios from "axios";
import LoginPage from "./page/auth/LoginPage";
import MyAccountPage from "./page/auth/MyAccount";
import ProposalMain from "./page/onandme_info_proposal/proposal/ProposalMain";
import ProposalUpload from "./page/onandme_info_proposal/proposal/ProposalUpload";
import {SpaceContentUploadPage} from "./page/onandme_info_proposal/poster/3d/SpaceContentUploader";
import {SpaceContentListPage} from "./page/onandme_info_proposal/poster/3d/SpaceContentListPage";
import PosterUploader from "./page/onandme_info_proposal/poster/2d/PosterUploader";
import PosterListPage from "./page/onandme_info_proposal/poster/2d/PosterListPage";
import EventVenuePage from "./page/onandme_info_proposal/event_venue/EventVenuePage";
import EmployeeManager from "./page/auth/admin/EmployeeManager";
import LoginModal from "./page/auth/LoginModal";
import OnandmeHeader from "./page/onandme_info_proposal/component/OnandmeInfoHeader";
import EventVenueUploadPage from "./page/onandme_info_proposal/event_venue/EventVenueUploadPage";
import QrEventAdminListPage from "./page/qr_events/admin/QrEventAdminListPage";
import QrEventParticipantRequestForm from "./page/qr_events/user/QrEventParticipantRequestForm";
import QrEventMyParticipantCheckPage from "./page/qr_events/user/QrEventMyParticipantCheckPage";
import QrEventParticipantMyView from "./page/qr_events/user/QrEventParticipantMyView";
import AttendancePage from "./page/qr_events/admin/AttendancePage";
import BzCardLanding from "./page/qr_events/biz_card/BizCardLanding";

export default function App() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [checkingToken, setCheckingToken] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [forceLogin, setForceLogin] = useState(false);

    const hostname = window.location.hostname.toLowerCase();
    const pathname = window.location.pathname;

    // ✅ onandme.info 로 들어온 경우만 "정상 렌더링"
    const isOnandmeHost = hostname === "onandme.info" || hostname === "localhost" || hostname.endsWith(".onandme.info");

    // ✅ 인증 없이 접근 가능한 경로 목록
    const PUBLIC_PATH_PREFIXES = [
        "/qr-event-participant-request",
        "/qr-event-participant-check",
        "/qr-event-participant-my-view",
        "/qr-event-participant-attendance",
    ];

    const NOT_USE_HEADER_PATH_PATH = ["/qr-event-participant-attendance"];

    // ✅ onandme.info가 아닐 땐(다른 호스트) 인증 로직을 아예 안 타게 하고 싶으면: 전부 public 취급
    const isPublicPage =
        !isOnandmeHost ||
        PUBLIC_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix)) ||
        pathname.includes("pdf");

    const isNotUseHeaderPage = NOT_USE_HEADER_PATH_PATH.some((prefix) =>
        pathname.startsWith(prefix)
    );

    // ✅ axios interceptor 중복 등록 방지
    const interceptorBoundRef = useRef(false);
    useEffect(() => {
        if (interceptorBoundRef.current) return;
        interceptorBoundRef.current = true;

        axios.interceptors.request.use((config) => {
            const token = localStorage.getItem("auth_token");
            if (token && !config.headers["X-TOKEN"]) {
                config.headers["X-TOKEN"] = token;
            }
            return config;
        });

        axios.interceptors.response.use(
            (res) => res,
            (err) => {
                if (err.response?.status === 401) {
                    console.warn("⛔ 세션 만료로 재로그인 유도");
                    localStorage.removeItem("auth_token");
                    setIsAuthenticated(false);
                    setForceLogin(true);
                }
                return Promise.reject(err);
            }
        );
    }, []);

    const verifyToken = async () => {
        const token = localStorage.getItem("auth_token");

        if (!token) {
            setCheckingToken(false);
            setIsAuthenticated(false);
            return;
        }

        try {
            const res = await axios.post(`${process.env.REACT_APP_API_REAL_URL}/auth/verify`, { token });
            setIsAuthenticated(res.data.valid);
        } catch {
            setIsAuthenticated(false);
        } finally {
            setCheckingToken(false);
        }

        try {
            const res = await axios.post(`${process.env.REACT_APP_API_REAL_URL}/auth/verify_admin`, { token });
            setIsAdmin(res.data.valid);
        } catch {
            setIsAdmin(false);
        }
    };

    const handleLogout = async () => {
        const token = localStorage.getItem("auth_token");
        await fetch(`${process.env.REACT_APP_API_REAL_URL}/auth/logout`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-TOKEN": token,
            },
        });
        localStorage.removeItem("auth_token");
        setIsAuthenticated(false);
        setIsAdmin(false);
        setForceLogin(true);
    };

    useEffect(() => {
        // ✅ onandme.info가 아닐 땐(다른 호스트) 로그인 체크 자체를 생략
        if (!isOnandmeHost) {
            setCheckingToken(false);
            return;
        }

        if (!isPublicPage) verifyToken();
        else setCheckingToken(false);

        // eslint-disable-next-line
    }, []);

    // ✅ 세션 확인 중 (onandme.info에서만 의미 있음)
    if (checkingToken) {
        return (
            <div className="flex items-center justify-center min-h-screen text-gray-500 text-lg">
                로그인 상태 확인 중...
            </div>
        );
    }

    // ✅ 인증 필요: 로그인 모달 (onandme.info에서만 의미 있음)
    if ((!isAuthenticated || forceLogin) && !isPublicPage) {
        return (
            <LoginModal
                onLoginSuccess={() => {
                    setForceLogin(false);
                    verifyToken();
                }}
            />
        );
    }

    // ✅ 정상 렌더링
    return (
        <BrowserRouter>
            {/* ================================
          ✅ CSS 영향 안 받는 쪽 (상단)
          - onandme.info가 아니면 여기서만 라우팅 끝냄
      ================================ */}

            {/* 기존처럼 단독 페이지 */}
            <Route exact path="/qr-event-participant-attendance" component={AttendancePage} />

            {/* ✅ onandme.info가 아니면(onnme.in 등) 여기서 “단독 Switch”로 처리 */}
            {!isOnandmeHost && (
                <Switch>
                    {/* onnme.in + /test => A 단독 */}
                    {/*<Route path="/test" component={A} />*/}

                    {/* ✅ onnme.in/bzcard=개인코드 */}
                    <Route path="/bizcard=:code" component={BzCardLanding} />

                    {/* 필요하면 다른 호스트용 라우트도 여기서만 추가 */}
                    {/* <Route exact path="/" component={SomeLanding}/> */}
                </Switch>
            )}

            {/* ================================
          ✅ 정상 렌더링 (onandme.info 전용)
      ================================ */}
                <div className="bg-gray-10 min-h-screen" translate="no">
                    {!isPublicPage && !pathname.startsWith("/onandme") && !isNotUseHeaderPage && (
                        <Header isAuthenticated={isAuthenticated} isAdmin={isAdmin} onLogout={handleLogout} />
                    )}

                    <Switch>
                        <main className="pt-20 max-w-8xl mx-auto px-4">
                            {/* Onandme 그룹 라우트 */}
                            <Route path="/onandme">
                                <OnandmeHeader />
                                <Switch>
                                    <Route exact path="/onandme/proposal" component={ProposalMain} />
                                    <Route exact path="/onandme/proposal/upload" component={ProposalUpload} />
                                    <Route exact path="/onandme/upload/3d" component={SpaceContentUploadPage} />
                                    <Route exact path="/onandme/list/3d" component={SpaceContentListPage} />
                                    <Route exact path="/onandme/upload/2d" component={PosterUploader} />
                                    <Route exact path="/onandme/list/2d" component={PosterListPage} />
                                    <Route exact path="/onandme/event-venue" component={EventVenuePage} />
                                    <Route exact path="/onandme/upload/event-venue" component={EventVenueUploadPage} />
                                </Switch>
                            </Route>

                            {/* 공개 페이지 */}
                            <Route exact path="/qr-event-participant-request/:eventName" component={QrEventParticipantRequestForm} />
                            <Route exact path="/qr-event-participant-check/:eventName" component={QrEventMyParticipantCheckPage} />
                            <Route exact path="/qr-event-participant-my-view" component={QrEventParticipantMyView} />

                            {/* 관리자 */}
                            <Route exact path="/qr-event-admin-list" component={QrEventAdminListPage} />

                            {/* 일반 라우트 */}
                            <Route exact path="/" component={MainSearchPage} />
                            <Route path="/search/companies/" component={CompanySearchPage} />
                            <Route exact path="/employees" component={EmployeeManager} />
                            <Route exact path="/login" component={LoginPage} />
                            <Route exact path="/me" render={() => <MyAccountPage onLogout={handleLogout} />} />
                        </main>
                    </Switch>
                </div>
        </BrowserRouter>
    );
}